import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { t } from 'Locale/index';
import { Device, lockToPortrait } from 'DeviceUtils';
import Theme from 'Theme';
import NavigationService from 'NavigationService';
import { ScrollView, Text, Pressable, View } from 'react-native';
import { Icon } from 'LocalGluons/index';
import { useStore } from 'Store';
import { logEvent } from 'AppManager';
import { useSettingsNavigation } from 'Pages/Settings/components/SettingsNavigationContext/SettingsNavigationContext';
import { useNavigation } from '@react-navigation/native';
import { EPG_LIST_TYPES } from '../../../../../../../options';

const ListFilterPage = ({
    theme,
    listMode,
    onClose,
    onSelected,
    openCategories,
    allChannels,
    liveChannels,
    favoriteChannels,
    onReset,
    hideChannelListTypeAll
}) => {
    const { userData } = useStore();
    const navigation = useNavigation();
    const { setDefaultOptionRoute, setPreviousRoute, setReturnToPrevious } =
        useSettingsNavigation();
    const onManageFavoritesPress = () => {
        onClose();
        setReturnToPrevious(true);
        onSelected(EPG_LIST_TYPES.FAVORITES);
        if (Device.isPhone) {
            lockToPortrait();
            navigation.navigate('FavoriteSettings');
        }
        if (Device.isTabletOrWeb) {
            if (Device.isTabletOrWeb) setPreviousRoute('TvGuide');
            setDefaultOptionRoute('FavoriteSettings');
            navigation.navigate('settings');
        }
    };

    const onOpenCategories = useCallback(
        async (type) => {
            if (type !== listMode) {
                await onSelected(type);
            }
            openCategories(type);
        },
        [onSelected, openCategories]
    );

    const onListModeSelected = (selectedListMode) => {
        if (selectedListMode !== listMode) onSelected(selectedListMode);
    };

    const trashCanSize = Device.isPhone ? 30 : 20;

    return (
        <ScrollView>
            <View
                style={[
                    theme.window.line,
                    {
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }
                ]}
            >
                {Device.isPhone && <View style={{ width: 30 + Theme.BU }} />}
                <Text
                    numberOfLines={1}
                    style={[theme.window.lineText, { fontSize: 22, maxWidth: '100%' }]}
                >
                    {t('EPG.FILTER_BY_CHANNELS')}
                </Text>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Icon
                        name="delete_blue"
                        style={{
                            width: trashCanSize,
                            height: trashCanSize,
                            marginRight: Theme.BU / 2
                        }}
                        onPress={onReset}
                        testID="delete_filter"
                    />
                    <Icon
                        name="close_grey"
                        style={{
                            width: 30,
                            height: 30,
                            marginRight: Theme.BU / 2
                        }}
                        onPress={onClose}
                        testID="close_button"
                    />
                </View>
            </View>
            <>
                <Pressable
                    key={`list-item-${EPG_LIST_TYPES.LIVE}`}
                    style={[theme.window.line, { flexDirection: 'row' }]}
                    onPress={() => onListModeSelected(EPG_LIST_TYPES.LIVE)}
                >
                    <View style={theme.window.filterRowItem}>
                        <Text
                            numberOfLines={1}
                            style={[
                                theme.window.lineText,
                                listMode === EPG_LIST_TYPES.LIVE && theme.window.lineTextActive
                            ]}
                        >
                            {t('COMMON.CHANNEL_MAP_LIVE')}
                        </Text>
                        <Text
                            style={theme.window.categoryItemCount}
                        >{`(${liveChannels.length})`}</Text>
                    </View>
                    <Icon
                        tintColor={Theme.secondaryTextColor}
                        name="arrow_right"
                        onPress={() => {
                            onOpenCategories(EPG_LIST_TYPES.LIVE);
                        }}
                        testID={`filter_${EPG_LIST_TYPES.LIVE}`}
                    />
                </Pressable>
                {!hideChannelListTypeAll && (
                    <Pressable
                        key={`list-item-${EPG_LIST_TYPES.ALL}`}
                        style={[theme.window.line, { flexDirection: 'row' }]}
                        onPress={() => {
                            onListModeSelected(EPG_LIST_TYPES.ALL);
                            logEvent({
                                type: 'user-flow',
                                subtype: 'epg',
                                content1: 'epg-filter',
                                content2: 'iptv',
                                context: '/epg'
                            });
                        }}
                    >
                        <View style={theme.window.filterRowItem}>
                            <Text
                                numberOfLines={1}
                                style={[
                                    theme.window.lineText,
                                    listMode === EPG_LIST_TYPES.ALL && theme.window.lineTextActive
                                ]}
                            >
                                {t('COMMON.CHANNEL_MAP_ALL')}
                            </Text>
                            <Text
                                style={theme.window.categoryItemCount}
                            >{`(${allChannels.length})`}</Text>
                        </View>
                    </Pressable>
                )}
                {userData && (
                    <Pressable
                        key={`list-item-${EPG_LIST_TYPES.FAVORITES}`}
                        style={[theme.window.line, { flexDirection: 'row' }]}
                        onPress={() => {
                            onListModeSelected(EPG_LIST_TYPES.FAVORITES);
                            logEvent({
                                type: 'user-flow',
                                subtype: 'epg',
                                content1: 'epg-filter',
                                content2: 'show-favourites',
                                context: '/epg'
                            });
                        }}
                    >
                        <View style={theme.window.filterRowItem}>
                            <Icon name="favorite_on_grey" style={theme.window.icon} />
                            <Text
                                numberOfLines={1}
                                style={[
                                    theme.window.lineText,
                                    listMode === EPG_LIST_TYPES.FAVORITES &&
                                        theme.window.lineTextActive
                                ]}
                            >
                                {t('COMMON.CHANNEL_MAP_FAVOURITE')}
                            </Text>
                            <Text
                                style={theme.window.categoryItemCount}
                            >{`(${favoriteChannels.length})`}</Text>
                        </View>
                        <Pressable onPress={onManageFavoritesPress}>
                            <Text
                                style={theme.window.lineTextActive}
                                testID="filter_favorites_edit"
                            >
                                {t('COMMON.MANAGE')}
                            </Text>
                        </Pressable>
                        <Icon
                            tintColor={Theme.secondaryTextColor}
                            name="arrow_right"
                            onPress={() => {
                                onOpenCategories(EPG_LIST_TYPES.FAVORITES);
                            }}
                            testID="filter_favorites"
                        />
                    </Pressable>
                )}
            </>
        </ScrollView>
    );
};

ListFilterPage.defaultProps = {
    theme: {},
    hideChannelListTypeAll: false
};

ListFilterPage.propTypes = {
    theme: PropTypes.shape({}),
    listMode: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    onSelected: PropTypes.func.isRequired,
    openCategories: PropTypes.func.isRequired,
    hideChannelListTypeAll: PropTypes.bool,
    allChannels: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    liveChannels: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    favoriteChannels: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    onReset: PropTypes.func.isRequired
};

export default ListFilterPage;
